import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Heart, TrendingUp, AlertCircle, CheckCircle2, ArrowUpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { HealthScoreGauge } from './HealthScoreGauge';
import { calculateFinancialHealthScore } from '@/utils/supreme-intelligence/financialHealthScore';
import type { EnhancedUnifiedFormData } from '@/validation/enhancedUnifiedSchemas';

interface FinancialHealthDashboardProps {
  formData: EnhancedUnifiedFormData;
  mortgagePayment: number;
}

export const FinancialHealthDashboard: React.FC<FinancialHealthDashboardProps> = ({
  formData,
  mortgagePayment,
}) => {
  const healthScore = useMemo(
    () => calculateFinancialHealthScore(formData, mortgagePayment),
    [formData, mortgagePayment]
  );

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <AlertCircle className="w-4 h-4" />;
      case 'medium': return <TrendingUp className="w-4 h-4" />;
      case 'low': return <ArrowUpCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Heart className="w-6 h-6 text-primary" />
          <h3 className="text-2xl font-bold">Financial Health Score</h3>
        </div>
        <p className="text-muted-foreground">
          Comprehensive analysis of your financial readiness
        </p>
      </div>

      {/* Main Gauge */}
      <Card className="bg-gradient-to-br from-card to-card/50 border-primary/20">
        <CardContent className="pt-8 pb-6">
          <HealthScoreGauge
            score={healthScore.totalScore}
            grade={healthScore.grade}
            color={healthScore.color}
          />
        </CardContent>
      </Card>

      {/* Component Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Object.entries(healthScore.components).map(([key, component], index) => (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium flex items-center justify-between">
                  <span>{component.label}</span>
                  <Badge variant="outline" className="text-xs">
                    {component.weight}%
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-2xl font-bold">{component.score}</span>
                  <span className="text-sm text-muted-foreground">/100</span>
                </div>
                <Progress value={component.score} className="h-2" />
                <div className="text-sm text-muted-foreground">
                  Current: <span className="font-medium text-foreground">{component.value}</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Strengths & Improvements */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card className="border-chart-2/50 bg-chart-2/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-chart-2">
              <CheckCircle2 className="w-5 h-5" />
              Strengths
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {healthScore.strengths.map((strength, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-start gap-2 text-sm"
                >
                  <CheckCircle2 className="w-4 h-4 text-chart-2 mt-0.5 flex-shrink-0" />
                  <span>{strength}</span>
                </motion.li>
              ))}
            </ul>
          </CardContent>
        </Card>

        <Card className="border-chart-5/50 bg-chart-5/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-chart-5">
              <AlertCircle className="w-5 h-5" />
              Areas for Improvement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {healthScore.improvements.length > 0 ? (
                healthScore.improvements.map((improvement, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + index * 0.1 }}
                    className="flex items-start gap-2 text-sm"
                  >
                    <AlertCircle className="w-4 h-4 text-chart-5 mt-0.5 flex-shrink-0" />
                    <span>{improvement}</span>
                  </motion.li>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">Excellent! No major areas of concern.</p>
              )}
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Action Items */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Recommended Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {healthScore.actionItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                className="flex gap-4 p-4 rounded-lg bg-muted/50 border border-border"
              >
                <div className="flex-shrink-0">
                  <Badge variant={getPriorityColor(item.priority)} className="gap-1">
                    {getPriorityIcon(item.priority)}
                    {item.priority}
                  </Badge>
                </div>
                <div className="flex-1 space-y-1">
                  <p className="font-medium">{item.action}</p>
                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      Impact: {item.impact}
                    </span>
                    <span>Timeline: {item.timeframe}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 12-Month Projection */}
      <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/30">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground mb-1">12-Month Projection</p>
              <p className="text-lg">
                Complete these actions to reach a score of{' '}
                <span className="text-2xl font-bold text-primary">
                  {healthScore.projectedScore12Months}
                </span>
              </p>
            </div>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 1, type: 'spring' }}
            >
              <TrendingUp className="w-12 h-12 text-primary" />
            </motion.div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
